// src/lib/sat/accommodations.ts
//
// Test accommodations (#830): self-serve accessibility settings that adjust a
// full-length SAT attempt — extended time (1.5x / 2x), a screen reader
// (read-aloud), and large print. Pure helpers only (no Prisma / React) so the
// SAT engine (start + finish-module), the results/runner UI, and the settings
// API all share one definition of the shape, the validator, and the
// extended-time multiplier.

export type ExtendedTime = 'NONE' | 'TIME_AND_HALF' | 'DOUBLE'

export type AccommodationFlags = {
  /** Extended testing time: none, time-and-a-half (1.5x), or double (2x). */
  extendedTime: ExtendedTime
  /** Reader: auto-enable the read-aloud (TTS) control in the runner. */
  reader: boolean
  /** Large print: start the runner at the largest text-zoom level. */
  largePrint: boolean
}

export const DEFAULT_ACCOMMODATIONS: AccommodationFlags = {
  extendedTime: 'NONE',
  reader: false,
  largePrint: false,
}

const EXTENDED_TIME_MULTIPLIER: Record<ExtendedTime, number> = {
  NONE: 1,
  TIME_AND_HALF: 1.5,
  DOUBLE: 2,
}

const EXTENDED_TIMES: readonly ExtendedTime[] = ['NONE', 'TIME_AND_HALF', 'DOUBLE']

/** Type guard for a well-formed AccommodationFlags object (used by the API). */
export function isValidAccommodations(obj: unknown): obj is AccommodationFlags {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false
  const o = obj as Record<string, unknown>
  return (
    EXTENDED_TIMES.includes(o.extendedTime as ExtendedTime) &&
    typeof o.reader === 'boolean' &&
    typeof o.largePrint === 'boolean'
  )
}

/**
 * Coerce an untrusted `User.accommodationFlags` JSON value into a complete
 * AccommodationFlags object, falling back to the default for anything missing
 * or malformed. Defensive (like normalize-choices): never throws.
 */
export function parseAccommodations(json: unknown): AccommodationFlags {
  if (!json || typeof json !== 'object' || Array.isArray(json)) {
    return { ...DEFAULT_ACCOMMODATIONS }
  }
  const o = json as Record<string, unknown>
  return {
    extendedTime: EXTENDED_TIMES.includes(o.extendedTime as ExtendedTime)
      ? (o.extendedTime as ExtendedTime)
      : 'NONE',
    reader: o.reader === true,
    largePrint: o.largePrint === true,
  }
}

/** The time multiplier (1 / 1.5 / 2) for a set of flags. */
export function extendedTimeMultiplier(flags: AccommodationFlags | null | undefined): number {
  if (!flags) return 1
  return EXTENDED_TIME_MULTIPLIER[flags.extendedTime] ?? 1
}

/**
 * Scale a base module duration (seconds) by a stored time multiplier, rounded
 * to whole seconds. A null/invalid/≤0 multiplier is treated as 1x, so legacy
 * attempts (timeMultiplier = null) and non-accommodated attempts are unchanged.
 */
export function scaleDurationSec(baseSec: number, multiplier: number | null | undefined): number {
  const m = typeof multiplier === 'number' && multiplier > 0 ? multiplier : 1
  return Math.round(baseSec * m)
}

// ── Score-report disclosure ────────────────────────────────────────────────
//
// An extended-time score is NOT comparable to standard-time national
// percentiles, College Board readiness benchmarks, or the college
// middle-50% ranges the report shows next to it — those norms are all
// standard-time populations. The report used to show those comparisons with
// no indication the clock had been extended, which quietly overstated the
// student's standing. Any surface that renders a percentile, a benchmark, or
// a college band for an attempt MUST also render this disclosure when the
// attempt carries extended time.
//
// Keyed off the ATTEMPT's frozen timeMultiplier, never the user's current
// flags: the multiplier is what actually governed that sitting, and a student
// who later turns extended time off must not retroactively see a
// standard-time report for a test they took with 2x.

/** Human label for a frozen attempt multiplier ("1.5x", "2x"), or null at 1x. */
export function timeMultiplierLabel(multiplier: number | null | undefined): string | null {
  if (typeof multiplier !== 'number' || !Number.isFinite(multiplier) || multiplier <= 1) return null
  // 1.5 → "1.5x"; 2 → "2x" (no trailing ".0").
  return `${Number.isInteger(multiplier) ? multiplier : multiplier.toFixed(1)}x`
}

/** True when this attempt was taken with extended time. */
export function hadExtendedTime(multiplier: number | null | undefined): boolean {
  return timeMultiplierLabel(multiplier) !== null
}

/**
 * The disclosure sentence for an attempt taken with extended time. Returns
 * null for standard-time attempts so callers omit the line entirely.
 */
export function accommodationDisclosure(multiplier: number | null | undefined): string | null {
  const label = timeMultiplierLabel(multiplier)
  if (!label) return null
  return (
    `This test was taken with extended time (${label}). Percentiles, readiness ` +
    `benchmarks, and college score ranges shown here come from standard-time ` +
    `populations, so they are not a like-for-like comparison.`
  )
}

/** Short badge text for the same condition ("Extended time · 1.5x"). */
export function accommodationBadge(multiplier: number | null | undefined): string | null {
  const label = timeMultiplierLabel(multiplier)
  return label ? `Extended time · ${label}` : null
}
